import React from 'react';
import { Building2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { calculateIfi } from '../utils/taxCalculations';

export default function IfiCalculator({ data, onChange }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const handleFieldChange = (field, val) => {
    onChange(prev => ({ ...prev, [field]: val }));
  };

  const ifiResult = calculateIfi(
    data.ifiResidencePrincipale || 0,
    data.ifiLocatifSecondaire || 0,
    data.ifiScpi || 0,
    data.ifiDettes || 0
  );

  return (
    <div className="space-y-6">
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Simulateur IFI (Impôt sur la Fortune Immobilière)</h3>
              <p className="text-xs text-subtle">Seuil de déclenchement à 1 300 000 € de patrimoine immobilier net taxable.</p>
            </div>
          </div>
          {ifiResult.assujetti ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300 text-xs font-bold">
              <AlertTriangle className="w-3.5 h-3.5" /> Foyer Assujetti à l'IFI
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300 text-xs font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Foyer Non Assujetti (Sous 1.3 M€)
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Résidence Principale Brute (€)</label>
            <Input
              type="number"
              min="0"
              value={data.ifiResidencePrincipale || ''}
              onChange={(e) => handleFieldChange("ifiResidencePrincipale", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
            <span className="text-[11px] text-success-600 font-medium mt-1 block">Abattement légal de 30% déduit</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Secondaires & Locatif Net (€)</label>
            <Input
              type="number"
              min="0"
              value={data.ifiLocatifSecondaire || ''}
              onChange={(e) => handleFieldChange("ifiLocatifSecondaire", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
            <span className="text-[11px] text-subtle mt-1 block">Biens détenus en direct ou SCI</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Parts de SCPI / OPCI (€)</label>
            <Input
              type="number"
              min="0"
              value={data.ifiScpi || ''}
              onChange={(e) => handleFieldChange("ifiScpi", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
            <span className="text-[11px] text-subtle mt-1 block">Quote-part immobilière</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-danger-500 mb-1.5">Dettes Immobilières Déductibles (€)</label>
            <Input
              type="number"
              min="0"
              value={data.ifiDettes || ''}
              onChange={(e) => handleFieldChange("ifiDettes", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
              className="border-danger-200 focus:border-danger-500"
            />
            <span className="text-[11px] text-subtle mt-1 block">Capital restant dû au 1er janvier</span>
          </div>
        </div>

        {/* Synthèse IFI */}
        <div className="mt-6 pt-5 border-t border-workspace grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace">
            <span className="text-xs text-subtle">Actif Net Taxable IFI</span>
            <p className="text-xl font-black text-primary mt-1">{formatEuro(ifiResult.actifImmoNetTaxable)}</p>
          </div>

          <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace">
            <span className="text-xs text-subtle">Décote de Lissage (1.3M - 1.4M€)</span>
            <p className="text-xl font-black text-secondary mt-1">{ifiResult.decoteIfi > 0 ? `-${formatEuro(ifiResult.decoteIfi)}` : "0 €"}</p>
          </div>

          <div className="p-4 rounded-xl bg-warning-50/50 dark:bg-warning-900/10 border border-warning-200 dark:border-warning-800/40">
            <span className="text-xs font-bold text-warning-800 dark:text-warning-300">Impôt IFI Annuel Estimé</span>
            <p className="text-2xl font-black text-warning-600 mt-1">{formatEuro(ifiResult.ifiNet)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

